'use client';

import { useEffect, useState } from 'react';
import { DummyImage } from '@/service/api/Images/getDummyImage';
import { SliderRightBtn } from '@/ui/common/Slider/core/SliderRightBtn';
import { SliderLeftBtn } from '@/ui/common/Slider/core/SliderLeftBtn';
/* import { SliderDots } from '@/ui/common/Slider/core/SliderDots'; */
import { SliderPicture } from '@/ui/common/Slider/core/SliderPicture';
import { SliderVideo } from '@/ui/common/Slider/core/SliderVideo';
import { TImageAssets } from '@/domain/Images.domain';
import { getFormattedPrice } from '@/service/utils/getFormattedPrice';

type TSliderProps = {
    slides: TImageAssets[];
    className?: string;
    price?: number;
    oldPrice?: string;
    priceText?: string;
    children?: React.ReactNode;
    preSelect?: number;
    setPreSelect?: (index: number) => void;
};

export const Slider = ({
    slides,
    className,
    price,
    oldPrice,
    priceText,
    children,
    preSelect,
    setPreSelect,
}: TSliderProps) => {
    const [active, setActive] = useState<number>(0);
    const [toShow, setToShow] = useState<number>(1);
    const [isPlayVideo, setPlayVideo] = useState<boolean>(false);

    const onChangeSlide = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, cb: () => void) => {
        e.preventDefault();
        e.stopPropagation();
        setPlayVideo(false);
        cb();
    };

    useEffect(() => {
        if (typeof preSelect !== 'undefined' && preSelect !== active) {
            setActive(preSelect);
            setToShow(slides.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preSelect, active]);

    if (!slides || !slides.length)
        return <DummyImage className={`rounded-2xl ${className || ''}`} />;

    const sliderItems = slides.slice(0, toShow).map(({ srcSet, slug, type }, idx) =>
        type === 'video' ? (
            <SliderVideo
                key={`${slug}_${idx}`}
                isPlayVideo={isPlayVideo}
                link={srcSet[0].src}
                onClick={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPlayVideo(true);
                }}
            />
        ) : (
            <SliderPicture key={`${slug}_${idx}`} srcSet={srcSet} slug={slug} />
        )
    );

    return (
        <div
            className={`group relative h-full w-full overflow-hidden rounded-2xl ${
                className || ''
            }`}
            data-test-id="images-slider"
        >
            {!!children && (
                <div className="absolute bottom-0 left-0 right-0 top-0 z-[1] bg-gradient-category"></div>
            )}
            <div
                className={`slider-wrap relative flex h-full w-full transition duration-300`}
                style={{ transform: `translateX(-${active}00%)` }}
            >
                {sliderItems}
            </div>

            <div className="pointer-events-none absolute inset-0 bg-gradient-image"></div>
            {price && !!+price && (
                <div
                    data-test-id="slider-price"
                    className="absolute bottom-0 right-0 z-10 flex items-center rounded-tl-2xl bg-[rgba(13,209,197,0.80)] px-4 py-2 text-white md:px-6 md:py-3"
                >
                    <span className="tx-large-regular mr-2 md:mr-4">{priceText || 'From'}</span>
                    {!!oldPrice && (
                        <span className="mr-2 line-through">{getFormattedPrice(+oldPrice)}</span>
                    )}
                    <span className="tx-heading-2 text-[28px] md:text-[36px]">
                        {getFormattedPrice(+price)}
                    </span>
                </div>
            )}
            {!!children && <div className="absolute bottom-2 left-4 right-4 z-[1]">{children}</div>}
            {slides.length > 1 && (
                <>
                    <SliderLeftBtn
                        onClick={(e) => {
                            onChangeSlide(e, () => {
                                setActive((prev) => {
                                    const newIndex = prev === 0 ? slides.length - 1 : --prev;
                                    setPreSelect?.(newIndex);
                                    return newIndex;
                                });
                                if (toShow < slides.length) {
                                    setToShow(slides.length);
                                }
                            });
                        }}
                    />

                    <SliderRightBtn
                        onClick={(e) => {
                            onChangeSlide(e, () => {
                                setActive((prev) => {
                                    const newIndex = prev === slides.length - 1 ? 0 : ++prev;
                                    setPreSelect?.(newIndex);
                                    return newIndex;
                                });
                                if (toShow < slides.length) {
                                    setToShow((prev) => prev + 1);
                                }
                            });
                        }}
                    />

                    {/* {!children && (
                        <SliderDots
                            slideIndexes={slides.map((_, idx) => idx)}
                            onClick={(e, idx) => {
                                if (toShow < slides.length) {
                                    setToShow(slides.length);
                                }
                                onChangeSlide(e, () => setActive(idx));
                            }}
                            activeIdx={active}
                        />
                    )} */}
                </>
            )}
        </div>
    );
};
